import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  fetchLots,
  fetchUserBookings,
  createBooking,
  cancelBooking as apiCancelBooking,
  updateLot,
  deleteLot,
  debugUserBookings,
} from "../services/api";
import api from "../services/api";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "../styles/Dashboard.css";
import "./UserDashboard.css";
import placeholderImage from "../assets/placeholder-image.jpeg";
import MyAgencyInfo from "../components/MyAgencyInfo";

const UserDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("overview"); // Changed default to overview
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedLot, setSelectedLot] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [phases, setPhases] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState({
    phase: "",
    type: "",
    status: "",
  });
  const [bookingFilter, setBookingFilter] = useState("all"); // 'all', 'pending', 'approved', 'cancelled'
  const [searchQuery, setSearchQuery] = useState("");
  const [securityKey, setSecurityKey] = useState(
    Math.random().toString(36).substring(7)
  );
  const [lastUserId, setLastUserId] = useState(null);
  const [tabsVisible, setTabsVisible] = useState(false);
  const [processingBookingId, setProcessingBookingId] = useState(null);

  // Check if user is admin
  const isAdmin = currentUser?.role === "admin";

  // Toggle tabs visibility for mobile
  const toggleTabs = () => {
    setTabsVisible(!tabsVisible);
  };

  // Handle tab change
  const handleTabChange = (mode) => {
    setViewMode(mode);
    setTabsVisible(false); // Hide tabs after selection on mobile
  };

  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  const API_URL =
    currentHost === "localhost" || currentHost === "127.0.0.1"
      ? `${currentProtocol}//${currentHost}:5002/api`
      : `${currentProtocol}//${currentHost}/api`;

  // Helper function to get current user data from localStorage
  const getCurrentUserData = () => {
    const userJson = localStorage.getItem("user");
    let userData = {
      id: null,
      email: "",
      name: "",
      role: "",
    };

    try {
      if (userJson) {
        const parsedUser = JSON.parse(userJson);
        userData = {
          id: parsedUser._id ? parsedUser._id.toString() : null,
          email: parsedUser.email || "",
          name: parsedUser.name || "",
          role: parsedUser.role || "",
        };
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }

    return userData;
  };

  // Fetch user's bookings - moved outside useEffect so it can be called from other methods
  const loadBookings = async () => {
    try {
      setLoading(true);

      // Get the current user from both localStorage and sessionStorage
      const userJson = localStorage.getItem("user");
      const sessionUser = sessionStorage.getItem("currentSessionUser");

      if (!userJson) {
        console.error("No user data found in localStorage during loadBookings");
        setBookings([]);
        setLoading(false);
        setError("User data not found. Please try logging in again.");
        return;
      }

      let userId = null;
      let userData = null;

      try {
        userData = JSON.parse(userJson);
        if (userData && userData._id) {
          userId = userData._id.toString();

          // Verify user ID matches sessionStorage to prevent conflicts
          if (sessionUser && sessionUser !== userId) {
            console.error(
              "User ID mismatch - localStorage:",
              userId,
              "sessionStorage:",
              sessionUser
            );

            // Trust sessionStorage in this case (it's browser tab specific)
            if (sessionUser) {
              console.log("Using sessionStorage user ID instead:", sessionUser);
              userId = sessionUser;

              // Update the userData in localStorage to match sessionStorage
              userData._id = sessionUser;
              localStorage.setItem("user", JSON.stringify(userData));
              console.log("Updated localStorage user to match sessionStorage");
            }
          }

          console.log("loadBookings: Using user ID:", userId);
          
          // Store user ID in sessionStorage for consistency
          if (!sessionStorage.getItem("currentSessionUser")) {
            sessionStorage.setItem("currentSessionUser", userId);
          }
        } else {
          console.error("Invalid user data structure:", userData);
          setBookings([]);
          setLoading(false);
          setError("Invalid user data structure. Please log in again.");
          return;
        }
      } catch (e) {
        console.error("Error parsing user data from localStorage:", e);
        setBookings([]);
        setLoading(false);
        setError("Error accessing user data. Please log in again.");
        return;
      }

      // Now make the API call with the confirmed user ID
      console.log("Fetching bookings for user ID:", userId);
      let bookingsData = [];

      try {
        // First try the normal API call
        bookingsData = await fetchUserBookings();
        console.log(
          "Server returned bookings for user",
          userId,
          ":",
          bookingsData.length
        );
      } catch (apiError) {
        console.error("Error calling fetchUserBookings:", apiError);

        // If fetchUserBookings fails, try the debug function as fallback
        console.log("Trying debug bookings function as fallback");
        try {
          bookingsData = await debugUserBookings(userId);
          console.log(
            "Debug function returned bookings for user",
            userId,
            ":",
            bookingsData.length
          );
        } catch (debugError) {
          console.error("Debug bookings function also failed:", debugError);
          
          // Try to load from localStorage as last resort
          const cachedBookingsJson = localStorage.getItem("userBookings");
          if (cachedBookingsJson) {
            try {
              const cachedBookings = JSON.parse(cachedBookingsJson);
              if (Array.isArray(cachedBookings) && cachedBookings.length > 0) {
                console.log("Using cached bookings from localStorage:", cachedBookings.length);
                bookingsData = cachedBookings;
              }
            } catch (cacheError) {
              console.error("Error parsing cached bookings:", cacheError);
            }
          }
        }
      }

      // Set the bookings directly - the API ensures they belong to the current user
      setBookings(bookingsData);
      
      // Cache the bookings in localStorage for offline/fallback use
      try {
        localStorage.setItem("userBookings", JSON.stringify(bookingsData));
      } catch (storageError) {
        console.warn("Failed to cache bookings in localStorage:", storageError);
      }
      
      setLoading(false);
      
      // Clear any previous errors since we successfully loaded bookings
      if (error) setError("");
    } catch (err) {
      console.error("Error loading bookings:", err);
      setBookings([]);
      setLoading(false);
      setError("Failed to load your bookings. Please try again later.");
    }
  };

  useEffect(() => {
    // Clear session storage if a different user has logged in
    const userJson = localStorage.getItem("user");
    if (userJson) {
      try {
        const userObj = JSON.parse(userJson);
        const currentUserId = userObj._id;

        // Debug log to track user ID
        console.log("Current User ID detected in localStorage:", currentUserId);

        // If we detect a different user than before
        if (lastUserId && lastUserId !== currentUserId) {
          console.log("Different user detected - clearing session storage");
          sessionStorage.removeItem("userCreatedBookings");
          sessionStorage.removeItem("justCreatedBookingId");
          sessionStorage.setItem("userCreatedBookings", JSON.stringify([]));
        }

        // Update our reference
        setLastUserId(currentUserId);
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
      }
    }
  }, [currentUser, lastUserId]);

  // Add a debugging function to synchronize user data
  const synchronizeUserData = () => {
    // Clear any potential localStorage issues
    const authToken = localStorage.getItem("token");
    const userJson = localStorage.getItem("user");

    if (!authToken || !userJson) {
      console.error("No valid auth data in localStorage!");
      return false;
    }

    try {
      const userData = JSON.parse(userJson);
      if (!userData || !userData._id) {
        console.error("Invalid user data in localStorage:", userData);
        return false;
      }

      console.log("Synchronized user data with ID:", userData._id);
      return true;
    } catch (e) {
      console.error("Error parsing user data:", e);
      return false;
    }
  };

  useEffect(() => {
    // Initialize session storage for tracking bookings if needed
    if (!sessionStorage.getItem("userCreatedBookings")) {
      sessionStorage.setItem("userCreatedBookings", JSON.stringify([]));
    }

    // Synchronize user data first
    const userDataValid = synchronizeUserData();

    if (userDataValid) {
      loadLots();
      loadBookings(); // Initial load of bookings
    } else {
      console.error(
        "User data synchronization failed - may affect booking visibility"
      );
    }
  }, []);

  // Add this new useEffect to ensure bookings are loaded when the user changes or when viewMode changes to 'bookings'
  useEffect(() => {
    if (viewMode === "bookings") {
      loadBookings();
    }
  }, [viewMode, currentUser]);

  // Function to load lots data
  const loadLots = async () => {
    try {
      setLoading(true);
      const lotsData = await fetchLots();

      // Filter lots based on user role and selected phase
      let filteredLots = lotsData;

      // If user is not admin, only show available lots
      if (currentUser?.role !== "admin") {
        filteredLots = lotsData.filter((lot) => lot.status === "available");
      }

      // If a phase is selected, filter by that phase
      if (selectedPhase) {
        filteredLots = filteredLots.filter(
          (lot) => lot.phase === selectedPhase
        );
      }

      setLots(filteredLots);

      // Extract unique phases from lots
      const uniquePhases = [
        ...new Set(filteredLots.map((lot) => lot.phase)),
      ].sort((a, b) => a - b);
      setPhases(uniquePhases);

      setLoading(false);
      return true; // Indicate successful load
    } catch (err) {
      console.error("Error loading lots:", err);
      setError("Failed to load lots");
      setLoading(false);
      return false; // Indicate failed load
    }
  };

  const handleLotSelection = (lot) => {
    setSelectedLot(lot);
  };

  const handleBookingRequest = async (e) => {
    e.preventDefault();

    if (!selectedLot) {
      setError("Please select a lot to book");
      return;
    }

    try {
      const notes = e.target.elements.notes.value;

      // Create the booking with proper structure
      const bookingData = {
        unit: selectedLot._id,
        user: currentUser._id,
        date: new Date().toISOString().split("T")[0],
        notes: notes,
        contactInfo: {
          name: currentUser.name || "",
          phone: currentUser.phone || "",
          email: currentUser.email || "",
        },
        status: "pending",
      };

      // Create the booking
      const response = await createBooking(bookingData);

      // Success! Reset form and show success message
      e.target.reset();
      setSuccessMessage("Booking request submitted successfully!");

      // Set a timer to clear the success message
      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);

      // Switch to the bookings view and refresh bookings
      setViewMode("bookings");

      // Clear the selected lot
      setSelectedLot(null);

      // Reload lots to update availability status
      await loadLots();

      // Load bookings to show the new booking
      await loadBookings();
    } catch (err) {
      console.error("Booking error:", err);
      setError(err.message || "Failed to create booking");
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      setLoading(true);
      
      // Store the ID of the booking being processed
      setProcessingBookingId(bookingId);
      
      // Provide immediate UI feedback
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, isProcessing: true } 
            : booking
        )
      );
      
      setSuccessMessage("Processing cancellation request...");
      
      // Add debugging for the cancellation process
      console.log(`Attempting to cancel booking with ID: ${bookingId}`);
      
      // Get the current user data for debugging
      const userJson = localStorage.getItem("user");
      let userId = null;
      try {
        if (userJson) {
          const userData = JSON.parse(userJson);
          userId = userData._id;
          console.log(`User ${userId} is cancelling booking ${bookingId}`);
        }
      } catch (e) {
        console.error("Error getting user data during cancellation:", e);
      }

      // Call the API
      await apiCancelBooking(bookingId);
      
      // Update the UI - mark as cancelled
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, status: "cancelled", isProcessing: false } 
            : booking
        )
      );
      
      // Cache updated bookings in localStorage
      try {
        const updatedBookings = bookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, status: "cancelled", isProcessing: false } 
            : booking
        );
        localStorage.setItem("userBookings", JSON.stringify(updatedBookings));
      } catch (storageError) {
        console.warn("Failed to update cached bookings:", storageError);
      }
      
      setSuccessMessage("Booking was successfully cancelled.");
      
      // Clear success message after a few seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      
      // Clear processing booking ID
      setProcessingBookingId(null);
      setLoading(false);
    } catch (err) {
      console.error("Error cancelling booking:", err);
      
      // Restore booking state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, isProcessing: false } 
            : booking
        )
      );
      
      // Set error message
      setError(`Failed to cancel booking: ${err.message || "Unknown error"}`);
      
      // Clear error message after a few seconds
      setTimeout(() => {
        setError("");
      }, 5000);
      
      // Clear processing booking ID
      setProcessingBookingId(null);
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({ ...filter, [name]: value });
  };

  const handleBookingFilterChange = (newFilter) => {
    setBookingFilter(newFilter);
  };

  // Filter lots based on user selection
  const filteredLots = lots.filter((lot) => {
    // For regular users, only show available lots
    if (currentUser?.role !== "admin") {
      return (
        lot.status === "available" &&
        (filter.type === "" || lot.type === filter.type) &&
        (filter.phase === "" ||
          selectedPhase !== null ||
          lot.phase === parseInt(filter.phase))
      );
    }

    // For admin users, show all lots based on filter
    return (
      (filter.type === "" || lot.type === filter.type) &&
      (filter.status === "" || lot.status === filter.status) &&
      (filter.phase === "" ||
        selectedPhase !== null ||
        lot.phase === parseInt(filter.phase))
    );
  });

  const handlePhaseSelect = (phase) => {
    setSelectedPhase(phase);
    // For regular users, always set status to 'available' when a phase is selected
    if (currentUser?.role !== "admin") {
      setSelectedStatus("available");
    }
    // Set view mode to 'lots' to show the lots table
    setViewMode("lots");
    // Reload lots to apply the new filters
    loadLots();
  };

  const handleBackToPhases = () => {
    setSelectedPhase(null);
    setViewMode("phases");
    // Reset phase filter
    setFilter((prev) => ({ ...prev, phase: "" }));
  };

  const handleBookLot = (lot) => {
    if (currentUser?.role !== "admin") {
      navigate(`/book-lot/${lot._id}`);
    }
  };

  const handleEditLot = (lot) => {
    // For simplicity, let's redirect to the admin dashboard
    if (currentUser?.role === "admin") {
      navigate("/admin/lots");
    }
  };

  const handleDeleteLot = async (lotId) => {
    if (currentUser?.role === "admin") {
      if (window.confirm("Are you sure you want to delete this lot?")) {
        try {
          setLoading(true);
          await deleteLot(lotId);
          // Reload lots after deletion
          await loadLots();
          setLoading(false);
          setSuccessMessage("Lot deleted successfully");
        } catch (err) {
          console.error("Failed to delete lot:", err);
          setError("Failed to delete lot: " + (err.message || "Unknown error"));
          setLoading(false);
        }
      }
    }
  };

  const renderPhaseCards = () => {
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    if (phases.length === 0) {
      return <div className="no-data">No phases available</div>;
    }

    return (
      <div className="phase-cards-container">
        <h2>Select a Phase</h2>
        <div className="phase-cards">
          {phases.map((phase) => {
            const phaseLotsCount = lots.filter(
              (lot) => lot.phase === phase
            ).length;
            const availableLotsCount = lots.filter(
              (lot) => lot.phase === phase && lot.status === "available"
            ).length;

            return (
              <div
                key={phase}
                className="phase-card"
                onClick={() => handlePhaseSelect(phase)}
              >
                <h2>Phase {phase}</h2>
                <div className="phase-stats">
                  {currentUser?.role === "admin" ? (
                    <>
                      <div className="stat">
                        <span className="stat-value">{phaseLotsCount}</span>
                        <span className="stat-label">Total Lots</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{availableLotsCount}</span>
                        <span className="stat-label">Available</span>
                      </div>
                    </>
                  ) : (
                    <div className="stat">
                      <span className="stat-value">{availableLotsCount}</span>
                      <span className="stat-label">Available Lots</span>
                    </div>
                  )}
                </div>
                <button className="view-phase-btn">View Lots</button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLotsList = () => {
    const currentFilteredLots = selectedPhase
      ? filteredLots.filter((lot) => lot.phase === selectedPhase)
      : filteredLots;

    return (
      <div className="lots-section">
        <div className="section-header">
          <button className="back-button" onClick={handleBackToPhases}>
            ← Back to Phases
          </button>
          <h2>Phase {selectedPhase} Lots</h2>
        </div>

        <div className="filter-section">
          <h3>Filter Options</h3>

          <div className="filter-options">
            <div className="form-group">
              <label htmlFor="type">Lot Type</label>
              <select
                id="type"
                name="type"
                value={filter.type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="A">Type A</option>
                <option value="B">Type B</option>
                <option value="C">Type C</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={filter.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                {currentUser?.role === "admin" ? (
                  <>
                    <option value="available">Available</option>
                    <option value="reserved">Reserved</option>
                    <option value="sold">Sold</option>
                  </>
                ) : (
                  <option value="available">Available</option>
                )}
              </select>
            </div>
          </div>
        </div>

        <div className="factory-map">
          <h3>Factory Layout</h3>
          <div className="map-container">
            <img src={placeholderImage} alt="Factory Layout" />
          </div>
        </div>

        {loading ? (
          <p>Loading lots...</p>
        ) : currentFilteredLots.length === 0 ? (
          <p>No lots match your criteria.</p>
        ) : (
          <table className="lots-table">
            <thead>
              <tr>
                <th>Lot #</th>
                <th>Type</th>
                <th>Build Up (sqm)</th>
                <th>Land Size (sqm)</th>
                <th>Phase</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentFilteredLots.map((lot) => (
                <tr key={lot._id || lot.id}>
                  <td data-label="Lot #">{lot.lotNumber}</td>
                  <td data-label="Type">{lot.type}</td>
                  <td data-label="Build Up (sqm)">{lot.buildUp}</td>
                  <td data-label="Land Size (sqm)">{lot.landSize}</td>
                  <td data-label="Phase">{lot.phase}</td>
                  <td data-label="Status" className={`status-${lot.status}`}>
                    {lot.status}
                  </td>
                  <td data-label="Action">
                    {lot.status === "available" &&
                      currentUser?.role !== "admin" && (
                        <button
                          onClick={() => handleBookLot(lot)}
                          className="book-btn"
                        >
                          Book Now
                        </button>
                      )}
                    {currentUser?.role === "admin" &&
                      lot.status === "available" && (
                        <div className="admin-actions">
                          <button
                            onClick={() => handleEditLot(lot)}
                            className="edit-btn"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteLot(lot._id)}
                            className="delete-btn"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {selectedLot && (
          <div className="booking-section">
            <h2>Book Selected Lot</h2>

            <div className="selected-lot-info">
              <p>
                <strong>Lot Number:</strong> {selectedLot.lotNumber}
              </p>
              <p>
                <strong>Type:</strong> {selectedLot.type}
              </p>
              <p>
                <strong>Build Up:</strong> {selectedLot.buildUp} sqm
              </p>
              <p>
                <strong>Land Size:</strong> {selectedLot.landSize} sqm
              </p>
              <p>
                <strong>Phase:</strong> {selectedLot.phase}
              </p>
            </div>

            <button onClick={handleBookingRequest} className="book-button">
              Request Booking
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderUserBookings = () => {
    // Get user data using our helper
    const userData = getCurrentUserData();

    // Check if current user is an admin
    const isAdmin = userData.role === "admin";

    // Filter bookings based on selected filter and search query
    const filteredBookings = bookings.filter((booking) => {
      // Filter by status
      let statusMatch = true;
      if (bookingFilter === "pending") {
        statusMatch = booking.status === "pending" || 
                      booking.status === "pending_agent" || 
                      booking.status === "pending_admin";
      }
      if (bookingFilter === "approved") {
        statusMatch = booking.status === "approved" || 
                      booking.status === "confirmed";
      }
      if (bookingFilter === "cancelled") {
        statusMatch = booking.status === "cancelled" || 
                      booking.status === "rejected" ||
                      booking.status === "rejected_by_agent";
      }

      // Filter by search query
      let searchMatch = true;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const lotNumber =
          booking.lot?.lotNumber?.toString().toLowerCase() || "";
        const type = booking.lot?.type?.toLowerCase() || "";
        const phase = booking.lot?.phase?.toString().toLowerCase() || "";
        searchMatch =
          lotNumber.includes(query) ||
          type.includes(query) ||
          phase.includes(query);
      }

      return statusMatch && searchMatch;
    });

    // Count statuses for the stats cards
    const pendingCount = bookings.filter(
      b => b.status === "pending" || 
           b.status === "pending_agent" || 
           b.status === "pending_admin"
    ).length;
    
    const approvedCount = bookings.filter(
      b => b.status === "approved" || 
           b.status === "confirmed"
    ).length;
    
    const cancelledCount = bookings.filter(
      b => b.status === "cancelled" || 
           b.status === "rejected" || 
           b.status === "rejected_by_agent"
    ).length;

    return (
      <div className="user-bookings">
        <h2>{isAdmin ? "All Bookings" : "My Bookings"}</h2>

        <div className="booking-search">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchQuery("")}
              >
                ×
              </button>
            )}
          </div>

          <div className="actions-container">
            <div className="filter-dropdown-container">
              <select 
                className="filter-dropdown" 
                value={bookingFilter}
                onChange={(e) => handleBookingFilterChange(e.target.value)}
              >
                <option value="all">All Bookings ({bookings.length})</option>
                <option value="pending">Pending ({pendingCount})</option>
                <option value="approved">Approved ({approvedCount})</option>
                <option value="cancelled">Cancelled ({cancelledCount})</option>
              </select>
            </div>
            
            <button
              className="new-booking-btn"
              onClick={() => navigate("/properties")}
            >
              + New Booking
            </button>
          </div>
        </div>

        {bookings.length === 0 ? (
          <div className="no-bookings-message">
            <p>
              {isAdmin
                ? "No bookings found."
                : "You have no active bookings."}
            </p>
            <p className="booking-help">
              When you make a booking, it will appear here.
              <br />
              To make a booking, visit the Properties page and select a unit.
            </p>
          </div>
        ) : (
          <div>
            {filteredBookings.length > 0 ? (
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>Lot #</th>
                    <th>Type</th>
                    <th>Phase</th>
                    <th>Size</th>
                    <th>Creator</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => {
                    // Format date properly
                    const bookingDate = booking.createdAt
                      ? new Date(booking.createdAt)
                      : new Date();

                    // Format date string
                    const dateString = bookingDate.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });

                    // Format time string
                    const timeString = bookingDate.toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    return (
                      <tr
                        key={booking._id}
                        className={`status-${booking.status}`}
                      >
                        <td data-label="Lot #">{getPropertyName(booking)}</td>
                        <td data-label="Type">{getPropertyType(booking)}</td>
                        <td data-label="Phase">{getPropertyPhase(booking)}</td>
                        <td data-label="Size">{getPropertySize(booking)}</td>
                        <td data-label="Creator">
                          {booking.creatorEmail === userData.email
                            ? "You"
                            : booking.creatorName || "Unknown"}
                        </td>
                        <td data-label="Status">
                          <span className="status-badge">
                            {formatStatus(booking.status)}
                          </span>
                        </td>
                        <td data-label="Date">
                          <div className="date-time-info">
                            <span className="date-display">{dateString}</span>
                            <span className="time-display">{timeString}</span>
                          </div>
                        </td>
                        <td data-label="Action">
                          {booking.status !== "cancelled" &&
                          booking.status !== "rejected" &&
                          booking.status !== "rejected_by_agent" &&
                          booking.creatorEmail === userData.email ? (
                            <button
                              className="cancel-btn"
                              onClick={() => cancelBooking(booking._id)}
                              disabled={
                                booking._id === processingBookingId ||
                                booking.status === "cancelled"
                              }
                            >
                              {booking._id === processingBookingId
                                ? "Processing..."
                                : "Cancel"}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="no-bookings-message">
                <p>No bookings match your filter criteria.</p>
                {bookingFilter !== "all" || searchQuery ? (
                  <button
                    onClick={() => {
                      setBookingFilter("all");
                      setSearchQuery("");
                    }}
                    className="reset-filter-btn"
                  >
                    Reset Filters
                  </button>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Add a loading spinner component
  const LoadingSpinner = () => (
    <div className="loading-spinner">
      <div className="spinner"></div>
      <p>Loading data...</p>
    </div>
  );

  // Helper function to get the best available property name
  const getPropertyName = (booking) => {
    // Try to get the property name from various possible locations
    if (booking.propertyName) return booking.propertyName;
    
    // Check for lot information
    if (booking.lot) {
      if (booking.lot.lotNumber) return booking.lot.lotNumber;
      if (booking.lot.name) return booking.lot.name;
      if (booking.lot._id) return `Lot ${booking.lot._id}`;
    }
    
    // Check for unit information
    if (booking.unit) {
      if (booking.unit.lotNumber) return booking.unit.lotNumber;
      if (booking.unit.name) return booking.unit.name;
      if (booking.unit._id) return `Unit ${booking.unit._id}`;
    }
    
    // Check for property ID
    if (booking.propertyId) return `Property ${booking.propertyId}`;
    if (booking.lotId) return `Lot ${booking.lotId}`;
    if (booking.unitId) return `Unit ${booking.unitId}`;
    
    // Last resort
    return "N/A";
  };
  
  // Helper function to get the best available property type
  const getPropertyType = (booking) => {
    if (booking.propertyType) return booking.propertyType;
    
    // Check lot
    if (booking.lot && booking.lot.type) return booking.lot.type;
    
    // Check unit
    if (booking.unit && booking.unit.type) return booking.unit.type;
    
    // Default
    return "N/A";
  };
  
  // Helper function to get the best available property phase
  const getPropertyPhase = (booking) => {
    if (booking.propertyPhase) return booking.propertyPhase;
    if (booking.phase) return booking.phase;
    
    // Check lot
    if (booking.lot && booking.lot.phase) return booking.lot.phase;
    
    // Check unit
    if (booking.unit && booking.unit.phase) return booking.unit.phase;
    
    // Default
    return "N/A";
  };
  
  // Helper function to get the best available property size
  const getPropertySize = (booking) => {
    // Check for direct property size
    if (booking.buildUp) return `${booking.buildUp} sqft`;
    if (booking.landSize) return `${booking.landSize} sqft`;
    
    // Check lot build up
    if (booking.lot && booking.lot.buildUp) return `${booking.lot.buildUp} sqft`;
    
    // Check lot land size as fallback
    if (booking.lot && booking.lot.landSize) return `${booking.lot.landSize} sqft`;
    
    // Check unit build up
    if (booking.unit && booking.unit.buildUp) return `${booking.unit.buildUp} sqft`;
    
    // Check unit land size as fallback
    if (booking.unit && booking.unit.landSize) return `${booking.unit.landSize} sqft`;
    
    // Default
    return "N/A";
  };

  // Format status for display
  const formatStatus = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "pending_agent":
        return "Pending Agency Approval";
      case "pending_admin":
        return "Pending Admin Approval";
      case "approved":
        return "Approved";
      case "confirmed":
        return "Confirmed";
      case "rejected":
        return "Rejected";
      case "rejected_by_agent":
        return "Rejected by Agency";
      case "rejected_by_admin":
        return "Rejected by Admin";
      case "cancelled":
        return "Cancelled";
      case "expired":
        return "Expired";
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown";
    }
  };

  // Re-add the Overview tab function
  const renderOverview = () => {
    // Get stats for overview
    const pendingCount = bookings.filter(
      b => b.status === "pending" || 
           b.status === "pending_agent" || 
           b.status === "pending_admin"
    ).length;
    
    const approvedCount = bookings.filter(
      b => b.status === "approved" || 
           b.status === "confirmed"
    ).length;
    
    const cancelledCount = bookings.filter(
      b => b.status === "cancelled" || 
           b.status === "rejected" || 
           b.status === "rejected_by_agent"
    ).length;

    const userData = getCurrentUserData();
    
    return (
      <div className="dashboard-overview">
        <div className="overview-card">
          <h3>Agency Membership</h3>
          <div className="agency-info">
            <div className="info-row">
              <span className="info-label">Agency:</span>
              <span>Kajang Industrial Park</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className="status-approved">Approved</span>
            </div>
            <div className="info-row">
              <span className="info-label">Position:</span>
              <span>Property Agent</span>
            </div>
            <div className="info-row">
              <span className="info-label">Member Since:</span>
              <span>January 15, 2023</span>
            </div>
          </div>
          <button className="edit-profile-btn">Update Agency Details</button>
        </div>
        
        <div className="overview-card">
          <h3>My Profile</h3>
          <div className="user-info-card">
            <div className="info-row">
              <span className="info-label">Full Name:</span>
              <span>{userData.name || "Not provided"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Email Address:</span>
              <span>{userData.email || "Not provided"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Phone Number:</span>
              <span>{userData.phone || "Not provided"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Role:</span>
              <span>{userData.role || "User"}</span>
            </div>
          </div>
          <button className="edit-profile-btn">Edit Profile</button>
        </div>
      </div>
    );
  };

  // Main render method
  return (
    <div className="user-dashboard">
      <div className="dashboard-container">
        <div className="dashboard-content">
          {/* Welcome header */}
          <div className="welcome-header">
            <h1>Welcome, {currentUser?.name || "User"}</h1>
            <button 
              className="refresh-button" 
              onClick={synchronizeUserData}
              disabled={loading}
            >
              Refresh Data
            </button>
          </div>

          {/* Last refreshed indicator */}
          <div className="last-refreshed">
            Last refreshed: {new Date().toLocaleTimeString()}
          </div>

          {/* Horizontal tab navigation */}
          <div className="dashboard-tabs">
            <button
              className={`tab-btn ${viewMode === "overview" ? "active" : ""}`}
              onClick={() => handleTabChange("overview")}
            >
              Overview
            </button>
            <button
              className={`tab-btn ${viewMode === "bookings" ? "active" : ""}`}
              onClick={() => handleTabChange("bookings")}
            >
              My Bookings
            </button>
            <button
              className={`tab-btn ${viewMode === "profile" ? "active" : ""}`}
              onClick={() => handleTabChange("profile")}
            >
              My Profile
            </button>
          </div>

          {/* Error or success messages */}
          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          {/* Main content based on viewMode */}
          {loading ? (
            <LoadingSpinner />
          ) : viewMode === "overview" ? (
            renderOverview()
          ) : viewMode === "bookings" ? (
            renderUserBookings()
          ) : (
            <MyAgencyInfo user={currentUser} />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
